import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Chip,
    Grid,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import moment from "moment";
import { mapDispatchToProps } from "store/helpers/mapDispatch";
import { mapStateToProps } from "store/helpers/mapState";
import { connect } from "react-redux";
import { successToast } from "helpers/Notification";
import { errorToast } from "helpers/Notification";

function RoomsManagement({ SetSelectedMenu, systemSettings, location }) {
    const mainBackground = systemSettings?.mainBackground;

    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);

    const [openModal, setOpenModal] = useState(false);
    const [roomName, setRoomName] = useState("");
    const [addingRoom, setAddingRoom] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roomToDelete, setRoomToDelete] = useState(null);

    const [deletingRoom, setDeletingRoom] = useState(false);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [roomToEdit, setRoomToEdit] = useState(null);
    const [editRoomName, setEditRoomName] = useState("");
    const [editingRoom, setEditingRoom] = useState(false);




    const handleMenuOpen = (event, id) => {
        setMenuAnchor(event.currentTarget);
        setMenuOpenId(id);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuOpenId(null);
    };

    const handleEditRoom = (id) => {
        const room = rooms.find(room => room.id === id);
        if (room) {
            setRoomToEdit(id);
            setEditRoomName(room.name);
            setEditModalOpen(true);
        }
        handleMenuClose();
    };

    const handleDeleteRoom = (id) => {
        setRoomToDelete(id);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleAddRoom = async (e) => {
        e.preventDefault();
        setOpenModal(false);
        setAddingRoom(true);
        try {
            await createRoom(roomName);
            successToast("Created new room");
            await fetchRooms();
            setRoomName("");
        } catch (err) {
            errorToast("Failed to create room");
        } finally {
            setAddingRoom(false);
        }
    };

    const fetchRooms = async () => {
        setLoading(true);
        try {
            const response = await axios.get("/api/room/status");
            if (response.data && Array.isArray(response.data.room)) {
                setRooms(response.data.room);
            } else {
                setError("Failed to fetch rooms");
            }
        } catch (err) {
            setError("Error fetching rooms");
        } finally {
            setLoading(false);
        }
    };

    const createRoom = async (name) => {
        return axios.post("/api/room/single/create", { name });
        // return submitGeneralForm("/api/room/single/create", { name });
    };
    const updateRoom = async (id, newName) => {
        return axios.post(`/api/room/update/${id}`, { name: newName });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditingRoom(true);
        try {
            await updateRoom(roomToEdit, editRoomName);
            successToast("Room updated successfully");
            setRooms(prevRooms =>
                prevRooms.map(room =>
                    room.id === roomToEdit ? { ...room, name: editRoomName } : room
                )
            );
            setEditModalOpen(false);
        } catch (err) {
            successToast("Failed to update room");
        } finally {
            setEditingRoom(false);
        }
    };



    useEffect(() => {
        fetchRooms();
    }, []);

    return (
        <Box sx={{ p: 3, width: '100%' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">
                    Room Management
                </Typography>
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: mainBackground, color: "#fff", "&:hover": {
                            backgroundColor: mainBackground
                        }
                    }
                    }
                    startIcon={<AddIcon />}
                    onClick={() => setOpenModal(true)}
                >
                    Create Room
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error" sx={{ p: 2 }}>
                    {error}
                </Typography>
            ) : (
                <Grid container spacing={3}>
                    {rooms.map((room) => {
                        const isBooked = !!room.currentMeeting;
                        const start = isBooked ? moment(room.currentMeeting.start).format("hh:mm A") : null;
                        const end = isBooked ? moment(room.currentMeeting.end).format("hh:mm A") : null;
                        const createdBy = isBooked ? room.currentMeeting.createdBy : null;
                        const meetingType = isBooked ? room.currentMeeting.meetingtype : null;
                        return (
                            <Grid item xs={12} sm={6} md={6} key={room.id}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        p: 3,
                                        borderRadius: 3,
                                        transition: "transform 0.2s, box-shadow 0.2s",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: 6,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="h6" fontWeight={400} sx={{ flex: 1 }}>
                                            {room.name}
                                        </Typography>
                                        {isBooked ? (
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginRight: 2 }}>
                                                <Chip
                                                    label={`Booked: ${start} - ${end} By: ${createdBy.firstName} ${createdBy.lastName}`}
                                                    color="warning"
                                                    variant="filled"
                                                    sx={{ fontWeight: 500, marginBottom: 1 }}
                                                />
                                                <Chip
                                                    label={`Meeting Type: ${meetingType}`}
                                                    variant="outlined"
                                                    sx={{ color: "#336767", fontWeight: 500 }}
                                                />
                                            </Box>
                                        ) : (
                                            <Chip
                                                label="Available"
                                                color="success"
                                                variant="filled"
                                                sx={{ fontWeight: 500, marginRight: 2 }}
                                            />
                                        )}

                                        <IconButton
                                            aria-label="more"
                                            aria-controls={`menu-${room.id}`}
                                            aria-haspopup="true"
                                            onClick={(event) => handleMenuOpen(event, room.id)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            id={`menu-${room.id}`}
                                            anchorEl={menuAnchor}
                                            open={menuOpenId === room.id}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem onClick={() => handleEditRoom(room.id)}>Edit</MenuItem>
                                            <MenuItem onClick={() => handleDeleteRoom(room.id)}>Delete</MenuItem>
                                        </Menu>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>

            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Room</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this room?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>

                    <Button
                        color="error"
                        onClick={async () => {
                            setDeletingRoom(true);
                            try {
                                await axios.delete(`/api/room/${roomToDelete}`);
                                successToast("Room deleted successfully");
                                setRooms((prev) => prev.filter((room) => room.id !== roomToDelete));
                                setDeleteDialogOpen(false);
                            } catch (error) {
                                errorToast("Error deleting room");
                            } finally {
                                setDeletingRoom(false);
                            }
                        }}
                        disabled={deletingRoom}
                    >
                        {deletingRoom ? <CircularProgress size={20} color="error" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openModal} fullWidth={true} maxWidth={"xs"}>
                <DialogTitle>Create new Room</DialogTitle>
                <form onSubmit={handleAddRoom}>
                    <DialogContent>
                        <TextField
                            value={roomName}
                            onChange={(e) => setRoomName(e.target.value)}
                            placeholder={"Room name"}
                            fullWidth={true}
                            size={"small"}
                            label={"Room name"}
                            required={true}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button color={"error"} onClick={() => setOpenModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant={"contained"}
                            type={"submit"}
                            style={{ backgroundColor: mainBackground }}
                            disabled={addingRoom}
                        >
                            {addingRoom ? "Creating..." : "Create"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            <Dialog open={editModalOpen} fullWidth={true} maxWidth={"xs"} onClose={() => setEditModalOpen(false)}>
                <DialogTitle>Edit Room</DialogTitle>
                <form onSubmit={handleEditSubmit}>
                    <DialogContent>
                        <TextField
                            value={editRoomName}
                            onChange={(e) => setEditRoomName(e.target.value)}
                            placeholder={"Room name"}
                            fullWidth={true}
                            size={"small"}
                            label={"Room name"}
                            required={true}
                            autoFocus
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button color={"error"} onClick={() => setEditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant={"contained"}
                            type={"submit"}
                            style={{ backgroundColor: mainBackground }}
                            disabled={editingRoom}
                        >
                            {editingRoom ? "Updating..." : "Update"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );

}

export default connect(mapStateToProps, mapDispatchToProps)(RoomsManagement);
